import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ButtonComponent } from '../ui/button/button.component';
import { InterestPartyItem } from '../../models/trip-interest.types';
import { TripInterestService } from '../../services/trip-interest.service';
import { TripService } from '../../services/trip.service';
import { arrayToCsv, downloadCsv } from '../../utilities/csv-export';

let nextId = 0;

/**
 * "Grab the list": downloads every interested party for the current trip as a CSV,
 * including people already added to the roster (see the Status column).
 */
@Component({
  selector: 'app-interested-parties-export-button',
  imports: [TranslocoDirective, ButtonComponent],
  templateUrl: './interested-parties-export-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InterestedPartiesExportButtonComponent {
  private readonly tripInterest = inject(TripInterestService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);

  readonly tooltipId = `interested-parties-export-tooltip-${nextId++}`;

  // value() throws while the resource is in its error state, so check hasValue() first.
  private readonly parties = computed(() =>
    this.tripInterest.parties.hasValue() ? this.tripInterest.parties.value() : [],
  );
  readonly disabled = computed(
    () => this.tripInterest.parties.isLoading() || this.parties().length === 0,
  );

  download(): void {
    const tripId = this.tripService.currentTripId();
    if (tripId === null || this.disabled()) {
      return;
    }
    const t = (key: string) => this.transloco.translate(key);
    const csv = arrayToCsv<InterestPartyItem>(this.parties(), [
      { header: t(marker('group-management.interested-parties.export-first-name')), getValue: (r) => r.firstname },
      { header: t(marker('group-management.interested-parties.export-last-name')), getValue: (r) => r.lastname },
      { header: t(marker('group-management.interested-parties.export-email')), getValue: (r) => r.email },
      {
        header: t(marker('group-management.interested-parties.submitted')),
        getValue: (r) => formatDate(r.createdAt, 'MM/dd/yyyy', 'en-US'),
      },
      {
        header: t(marker('group-management.interested-parties.export-status')),
        getValue: (r) =>
          r.convertedAt !== null
            ? t(marker('group-management.interested-parties.converted'))
            : t(marker('group-management.interested-parties.export-status-pending')),
      },
    ]);
    downloadCsv(csv, `interested-parties-${tripId}`);
  }
}
